import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, studentApi, teacherApi, paymentApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { usePermissions } from '../components/Layout';
import {
  Users,
  GraduationCap,
  BookOpen,
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  UserPlus,
  ClipboardCheck,
  AlertCircle,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { cn } from '../lib/utils';

const COLORS = {
  primary: '#047857',
  secondary: '#1e3a8a',
  accent: '#d97706',
  muted: '#64748b',
};

export default function DashboardPage() {
  const { t, language } = useLanguage();
  const { canCreate, canView } = usePermissions();
  const [stats, setStats] = useState(null);
  const [recentStudents, setRecentStudents] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, studentsRes, paymentsRes] = await Promise.all([
          dashboardApi.getStats(),
          studentApi.getAll({ is_active: true }),
          paymentApi.getAll({}),
        ]);
        
        setStats(statsRes.data);
        setRecentStudents(studentsRes.data.slice(0, 5));
        setRecentPayments(paymentsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Erreur chargement dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const studentTypeData = stats ? [
    { name: 'Coranique', value: stats.quranic_students, color: COLORS.primary },
    { name: 'Classique', value: stats.classical_students, color: COLORS.secondary },
    { name: 'Mixte', value: stats.mixed_students, color: COLORS.accent },
  ] : [];

  const paymentData = [
    { name: 'Oct', collected: 2400000, pending: 400000 },
    { name: 'Nov', collected: 2800000, pending: 350000 },
    { name: 'Déc', collected: 2600000, pending: 500000 },
    { name: 'Jan', collected: 3000000, pending: 300000 },
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStudentTypeBadge = (type) => {
    const badges = {
      quranic: { label: 'Coranique', class: 'badge-quranic' },
      classical: { label: 'Classique', class: 'badge-classical' },
      mixed: { label: 'Mixte', class: 'badge-mixed' },
    };
    return badges[type] || { label: type, class: '' };
  };

  const getPaymentStatusBadge = (status) => {
    const statuses = {
      paid: { label: 'Payé', class: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700', icon: Clock },
      partial: { label: 'Partiel', class: 'bg-blue-100 text-blue-700', icon: AlertCircle },
      overdue: { label: 'En retard', class: 'bg-red-100 text-red-700', icon: AlertCircle },
    };
    return statuses[status] || statuses.pending;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">{t('dashboardTitle')}</h1>
          <p className="text-muted-foreground text-sm">
            {stats?.active_school_year ? `${language === 'ar' ? 'السنة الدراسية' : 'Année scolaire'}: ${stats.active_school_year}` : (language === 'ar' ? 'لا توجد سنة دراسية نشطة' : 'Aucune année scolaire active')}
          </p>
        </div>
        {canCreate('students') && (
          <div className="flex gap-2">
            <Button asChild className="rounded-full" data-testid="add-student-btn">
              <Link to="/students/new">
                <UserPlus className="w-4 h-4 mr-2" />
                {t('newStudent')}
              </Link>
            </Button>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-animation">
        <Card className="card-hover animate-slideUp" data-testid="stat-students">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">{t('totalStudents')}</p>
                <p className="text-3xl font-bold font-heading text-foreground mt-1">
                  {stats?.total_students || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.quranic_students || 0} {t('quranic')} • {stats?.classical_students || 0} {t('classical')}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover animate-slideUp" data-testid="stat-teachers">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">{t('teachers')}</p>
                <p className="text-3xl font-bold font-heading text-foreground mt-1">
                  {stats?.total_teachers || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.quranic_teachers || 0} {language === 'ar' ? 'قرآنيون' : 'Coraniques'} • {stats?.classical_teachers || 0} {language === 'ar' ? 'كلاسيكيون' : 'Classiques'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-secondary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover animate-slideUp" data-testid="stat-collected">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">{t('collectedPayments')}</p>
                <p className="text-2xl font-bold font-heading text-foreground mt-1">
                  {formatCurrency(stats?.total_payments_collected || 0)}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-green-600" />
                  <span className="text-xs text-green-600">+12% {language === 'ar' ? 'هذا الشهر' : 'ce mois'}</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover animate-slideUp" data-testid="stat-pending">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">{t('pendingPayments')}</p>
                <p className="text-2xl font-bold font-heading text-foreground mt-1">
                  {formatCurrency(stats?.pending_payments || 0)}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingDown className="w-3 h-3 text-amber-600" />
                  <span className="text-xs text-amber-600">{language === 'ar' ? 'للتحصيل' : 'À recouvrer'}</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts & Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Student Distribution */}
        <Card className="lg:col-span-4" data-testid="chart-distribution">
          <CardHeader>
            <CardTitle className="font-heading text-lg">{t('studentsByType')}</CardTitle>
            <CardDescription>{language === 'ar' ? 'حسب نوع التعليم' : 'Par type d\'enseignement'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={studentTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {studentTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [`${value} ${language === 'ar' ? 'طالب' : 'élèves'}`, '']}
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid hsl(var(--border))',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-4">
              {studentTypeData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-muted-foreground">{item.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Payment Chart */}
        <Card className="lg:col-span-8" data-testid="chart-payments">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading text-lg">{t('paymentOverview')}</CardTitle>
              <CardDescription>{language === 'ar' ? 'آخر 4 أشهر' : '4 derniers mois'}</CardDescription>
            </div>
            <Button variant="outline" size="sm" className="rounded-full" asChild>
              <Link to="/payments">
                {t('viewAll')}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={paymentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(v) => `${v / 1000000}M`}
                  />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value), '']}
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid hsl(var(--border))',
                    }}
                  />
                  <Bar dataKey="collected" name="Recettes" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pending" name="Impayés" fill={COLORS.accent} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Students */}
        <Card data-testid="recent-students">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading text-lg">Élèves récents</CardTitle>
              <CardDescription>Dernières inscriptions</CardDescription>
            </div>
            <Button variant="ghost" size="sm" className="rounded-full" asChild>
              <Link to="/students">
                Voir tout
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentStudents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucun élève inscrit</p>
                <Button variant="outline" size="sm" className="mt-2 rounded-full" asChild>
                  <Link to="/students/new">Inscrire un élève</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentStudents.map((student) => {
                  const badge = getStudentTypeBadge(student.student_type);
                  return (
                    <Link
                      key={student.id}
                      to={`/students/${student.id}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
                      data-testid={`student-${student.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {student.first_name[0]}{student.last_name[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {student.first_name} {student.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground">{student.matricule}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={cn('text-xs', badge.class)}>
                        {badge.label}
                      </Badge>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card data-testid="recent-payments">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading text-lg">Paiements récents</CardTitle>
              <CardDescription>Dernières transactions</CardDescription>
            </div>
            <Button variant="ghost" size="sm" className="rounded-full" asChild>
              <Link to="/payments">
                Voir tout
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentPayments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Wallet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucun paiement enregistré</p>
                <Button variant="outline" size="sm" className="mt-2 rounded-full" asChild>
                  <Link to="/payments">Gérer les paiements</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentPayments.map((payment) => {
                  const status = getPaymentStatusBadge(payment.status);
                  const StatusIcon = status.icon;
                  return (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', status.class)}>
                          <StatusIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {formatCurrency(payment.amount_paid)} / {formatCurrency(payment.amount_due)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {payment.month || 'Inscription'}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className={cn('text-xs', status.class)}>
                        {status.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions - Only show if user has create permissions */}
      {(canCreate('students') || canCreate('evaluations') || canCreate('payments') || canCreate('quran')) && (
        <Card data-testid="quick-actions">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Actions rapides</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {canCreate('students') && (
                <Button variant="outline" className="h-auto py-4 flex-col gap-2 rounded-xl" asChild>
                  <Link to="/students/new">
                    <UserPlus className="w-5 h-5 text-primary" />
                    <span className="text-sm">Nouvel élève</span>
                  </Link>
                </Button>
              )}
              {canCreate('evaluations') && (
                <Button variant="outline" className="h-auto py-4 flex-col gap-2 rounded-xl" asChild>
                  <Link to="/evaluations">
                    <ClipboardCheck className="w-5 h-5 text-secondary" />
                    <span className="text-sm">Évaluation</span>
                  </Link>
                </Button>
              )}
              {canCreate('payments') && (
                <Button variant="outline" className="h-auto py-4 flex-col gap-2 rounded-xl" asChild>
                  <Link to="/payments">
                    <Wallet className="w-5 h-5 text-accent" />
                    <span className="text-sm">Paiement</span>
                  </Link>
                </Button>
              )}
              {canCreate('quran') && (
                <Button variant="outline" className="h-auto py-4 flex-col gap-2 rounded-xl" asChild>
                  <Link to="/quran-progress">
                    <BookOpen className="w-5 h-5 text-primary" />
                    <span className="text-sm">Progression</span>
                  </Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
